import axios from "axios";
import { getCookie } from "cookies-next";
import { useEffect, useState, useCallback } from "react";
import { IoMdArrowBack } from "react-icons/io";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { PageLoading } from "../../../components";
import { API_ROOT } from "../../../config";
import "../../table.css";
import "./platformSignatureRecap.css";

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const LIMIT_OPTIONS = [10, 25, 50, 100];
const EMPTY_PAGINATION = { total: 0, page: 1, limit: 50, total_pages: 1 };

// ── Summary Cards ─────────────────────────────────────────────────────────────
const SummaryCards = ({ summary, filterLabel }) => {
  if (!summary) return null;

  const { all_time, filtered } = summary;

  const cards = [
    {
      title: "Periode Filter",
      subtitle:
        filterLabel?.from && filterLabel?.to
          ? `${filterLabel.from} s/d ${filterLabel.to}`
          : "Semua periode",
      items: [
        {
          label: "TTe Paperless",
          value: filtered?.total_paperless ?? 0,
          color: "text-blue-600",
        },
        {
          label: "TTe Markany",
          value: filtered?.total_markany ?? 0,
          color: "text-purple-600",
        },
        {
          label: "Total TTe",
          value: filtered?.total_tte ?? 0,
          color: "text-green-600",
          bold: true,
        },
      ],
      accent: "border-green-400",
      badge: "bg-green-50 text-green-700",
    },
    {
      title: "Keseluruhan",
      subtitle: "Sejak awal hingga sekarang",
      items: [
        {
          label: "TTe Paperless",
          value: all_time?.total_paperless ?? 0,
          color: "text-blue-600",
        },
        {
          label: "TTe Markany",
          value: all_time?.total_markany ?? 0,
          color: "text-purple-600",
        },
        {
          label: "Total TTe",
          value: all_time?.total_tte ?? 0,
          color: "text-green-600",
          bold: true,
        },
      ],
      accent: "border-gray-300",
      badge: "bg-gray-50 text-gray-600",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {cards.map((card) => (
        <div
          key={card.title}
          className={`rounded-xl border-l-4 ${card.accent} bg-white shadow-sm border border-gray-100 p-4`}
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-gray-800">{card.title}</p>
              <p className="text-xs text-gray-400 mt-0.5">{card.subtitle}</p>
            </div>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${card.badge}`}
            >
              {card.title === "Periode Filter" ? "Filter" : "All Time"}
            </span>
          </div>
          <div className="flex items-center divide-x divide-gray-100">
            {card.items.map((item) => (
              <div
                key={item.label}
                className="flex-1 px-3 first:pl-0 last:pr-0 text-center"
              >
                <p
                  className={`text-xl font-semibold ${item.color} ${item.bold ? "text-2xl" : ""}`}
                >
                  {Number(item.value).toLocaleString("id-ID")}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Pagination ────────────────────────────────────────────────────────────────
const Pagination = ({
  pagination,
  onPageChange,
  onLimitChange,
  currentLimit,
}) => {
  const { page, total_pages, total } = pagination;
  return (
    <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
      <div className="flex items-center gap-2">
        <span>Tampilkan</span>
        <select
          value={currentLimit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          className="border border-gray-300 rounded px-2 py-1"
        >
          {LIMIT_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <span>
          data · Total <strong>{total}</strong>
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
        >
          <BiChevronLeft size={20} />
        </button>
        {Array.from({ length: total_pages }, (_, i) => i + 1)
          .filter(
            (p) => p === 1 || p === total_pages || Math.abs(p - page) <= 1,
          )
          .reduce((acc, p, idx, arr) => {
            if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
            acc.push(p);
            return acc;
          }, [])
          .map((item, idx) =>
            item === "..." ? (
              <span key={`e-${idx}`} className="px-2">
                …
              </span>
            ) : (
              <button
                key={item}
                onClick={() => onPageChange(item)}
                className={`w-8 h-8 rounded text-sm ${
                  item === page
                    ? "bg-green-500 text-white"
                    : "hover:bg-gray-100"
                }`}
              >
                {item}
              </button>
            ),
          )}
        <button
          onClick={() => onPageChange(Math.min(total_pages, page + 1))}
          disabled={page >= total_pages}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
        >
          <BiChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

// ── DataTable ─────────────────────────────────────────────────────────────────
const DataTable = ({ columns, rows, loading }) => (
  <div className="overflow-x-auto mt-2">
    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
      <thead>
        <tr className="bg-gray-100 text-gray-700">
          {columns.map((col) => (
            <th
              key={col.key}
              className={`px-3 py-2 text-${col.align || "left"} whitespace-nowrap`}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td
              colSpan={columns.length}
              className="text-center py-8 text-gray-400"
            >
              Memuat data...
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length}
              className="text-center py-8 text-gray-400"
            >
              Tidak ada data
            </td>
          </tr>
        ) : (
          rows.map((row, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-3 py-2 text-${col.align || "left"} whitespace-nowrap`}
                >
                  {row[col.key] ?? "-"}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// ── Filter Bar ────────────────────────────────────────────────────────────────
const FilterBar = ({ filters, onChange, onFilter }) => (
  <div className="flex mb-4 gap-4 items-end">
    <div>
      <label className="block text-sm mb-1">Dari</label>
      <input
        type="date"
        value={filters.from}
        onChange={(e) =>
          onChange((prev) => ({ ...prev, from: e.target.value }))
        }
        className="block border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div>
      <label className="block text-sm mb-1">Sampai</label>
      <input
        type="date"
        value={filters.to}
        onChange={(e) => onChange((prev) => ({ ...prev, to: e.target.value }))}
        className="block border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <button
      className="text-white w-44 py-2 px-4 rounded-md text-sm bg-green-500 hover:bg-green-600"
      onClick={onFilter}
    >
      Filter
    </button>
  </div>
);

// ── Columns ───────────────────────────────────────────────────────────────────
const BY_DATE_COLUMNS = [
  { key: "no", label: "No", align: "center" },
  { key: "tanggal_sign", label: "Tanggal", align: "left" },
  { key: "jum_tte_paperless", label: "Jumlah TTe Paperless", align: "left" },
  { key: "jum_tte_markany", label: "Jumlah TTe Markany", align: "left" },
  { key: "total_tte", label: "Total TTe", align: "left" },
];

const BY_ASSIGNER_COLUMNS = [
  { key: "no", label: "No", align: "center" },
  { key: "tanggal_sign", label: "Tanggal", align: "left" },
  { key: "name_assiger", label: "Assigner", align: "left" },
  { key: "balance", label: "Balance", align: "left" },
  { key: "jum_tte_paperless", label: "Jumlah TTe Paperless", align: "left" },
  { key: "jum_tte_markany", label: "Jumlah TTe Markany", align: "left" },
  { key: "total_tte", label: "Total TTe", align: "left" },
];

// ── Main Component ────────────────────────────────────────────────────────────
const PlatformSignatureRecapDetails = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Ambil filter dari query params URL (dikirim dari halaman list)
  const today = new Date();
  const defaultFrom =
    searchParams.get("from") ||
    formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
  const defaultTo = searchParams.get("to") || formatDate(today);

  const [pageLoading, setPageLoading] = useState(true);
  const [detailData, setDetailData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("badan_usaha");

  const authHeader = { Authorization: getCookie("admin_auth") };

  // ── State Tab Badan Usaha ─────────────────────────────────────────────────
  const [buFilters, setBuFilters] = useState({
    from: defaultFrom,
    to: defaultTo,
  });
  const [buRows, setBuRows] = useState([]);
  const [buPagination, setBuPagination] = useState(EMPTY_PAGINATION);
  const [buLimit, setBuLimit] = useState(50);
  const [buLoading, setBuLoading] = useState(false);

  // ── State Tab Assigner ────────────────────────────────────────────────────
  const [asFilters, setAsFilters] = useState({
    from: defaultFrom,
    to: defaultTo,
  });
  const [asRows, setAsRows] = useState([]);
  const [asPagination, setAsPagination] = useState(EMPTY_PAGINATION);
  const [asLimit, setAsLimit] = useState(50);
  const [asLoading, setAsLoading] = useState(false);

  // ── Fetch header + summary ────────────────────────────────────────────────
  useEffect(() => {
    const fetchInitial = async () => {
      setPageLoading(true);
      try {
        const [detailRes, summaryRes] = await Promise.all([
          axios.get(`${API_ROOT}/admin/recap/platform-signatures/${uuid}`, {
            headers: authHeader,
          }),
          axios.get(
            `${API_ROOT}/admin/recap/platform-signatures/${uuid}/summary`,
            {
              params: { from: defaultFrom, to: defaultTo },
              headers: authHeader,
            },
          ),
        ]);
        setDetailData(detailRes.data.data.badan_usaha);
        setSummary(summaryRes.data.data);
        window.scrollTo({ top: 0 });
      } catch (err) {
        toast.error("Failed to load detail");
        if (err.response?.status === 401) navigate("/login");
      } finally {
        setPageLoading(false);
      }
    };
    fetchInitial();
  }, [uuid]);

  // ── Fetch By Date ─────────────────────────────────────────────────────────
  const fetchByDate = useCallback(
    async (page = 1, limit = buLimit, filters = buFilters) => {
      setBuLoading(true);
      try {
        const res = await axios.get(
          `${API_ROOT}/admin/recap/platform-signatures/${uuid}/by-date`,
          { params: { ...filters, page, limit }, headers: authHeader },
        );
        const { data, pagination } = res.data.data;
        setBuRows(
          data.map((item, idx) => ({
            ...item,
            no: (pagination.page - 1) * pagination.limit + idx + 1,
          })),
        );
        setBuPagination(pagination);
      } catch (err) {
        toast.error("Failed to load data");
        if (err.response?.status === 401) navigate("/login");
      } finally {
        setBuLoading(false);
      }
    },
    [uuid],
  );

  // ── Fetch By Assigner ─────────────────────────────────────────────────────
  const fetchByAssigner = useCallback(
    async (page = 1, limit = asLimit, filters = asFilters) => {
      setAsLoading(true);
      try {
        const res = await axios.get(
          `${API_ROOT}/admin/recap/platform-signatures/${uuid}/by-assigner`,
          { params: { ...filters, page, limit }, headers: authHeader },
        );
        const { data, pagination } = res.data.data;
        setAsRows(
          data.map((item, idx) => ({
            ...item,
            no: (pagination.page - 1) * pagination.limit + idx + 1,
          })),
        );
        setAsPagination(pagination);
      } catch (err) {
        toast.error("Failed to load data");
        if (err.response?.status === 401) navigate("/login");
      } finally {
        setAsLoading(false);
      }
    },
    [uuid],
  );

  // ── Load tab aktif ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!pageLoading) {
      if (activeTab === "badan_usaha") fetchByDate(1, buLimit, buFilters);
      else fetchByAssigner(1, asLimit, asFilters);
    }
  }, [activeTab, pageLoading]);

  // ── Filter juga refresh summary ───────────────────────────────────────────
  const handleBuFilter = async () => {
    const [, summaryRes] = await Promise.allSettled([
      fetchByDate(1, buLimit, buFilters),
      axios.get(`${API_ROOT}/admin/recap/platform-signatures/${uuid}/summary`, {
        params: { from: buFilters.from, to: buFilters.to },
        headers: authHeader,
      }),
    ]);
    if (summaryRes.status === "fulfilled")
      setSummary(summaryRes.value.data.data);
  };

  const handleAsFilter = async () => {
    const [, summaryRes] = await Promise.allSettled([
      fetchByAssigner(1, asLimit, asFilters),
      axios.get(`${API_ROOT}/admin/recap/platform-signatures/${uuid}/summary`, {
        params: { from: asFilters.from, to: asFilters.to },
        headers: authHeader,
      }),
    ]);
    if (summaryRes.status === "fulfilled")
      setSummary(summaryRes.value.data.data);
  };

  if (pageLoading) return <PageLoading />;

  const tabs = [
    { key: "badan_usaha", label: "By Badan Usaha" },
    { key: "assigner", label: "By Assigner" },
  ];

  return (
    <div className="flex flex-col gap-4 m-2 md:m-10 mt-24 px-2 py-10 md:p-10 bg-white rounded-3xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate("/recap/platform-signatures-recap")}>
          <IoMdArrowBack className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-medium">
          Platform Signature Recap Details
        </h1>
      </div>
      <hr />

      {/* Info */}
      <div className="flex flex-col gap-1">
        <div className="flex">
          <div className="flex-none w-40 bg-gray-600 text-white pl-2 py-1 flex items-center text-sm">
            Badan Usaha
          </div>
          <div className="flex-auto px-2 py-1 border border-gray-600 break-all text-sm">
            {detailData?.name}
          </div>
        </div>
        <div className="flex">
          <div className="flex-none w-40 bg-gray-600 text-white pl-2 py-1 flex items-center text-sm">
            Email
          </div>
          <div className="flex-auto px-2 py-1 border border-gray-600 break-all text-sm">
            {detailData?.email}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <SummaryCards summary={summary} filterLabel={summary?.filter_label} />

      <hr />

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? "border-b-2 border-green-500 text-green-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: By Badan Usaha */}
      {activeTab === "badan_usaha" && (
        <div>
          <FilterBar
            filters={buFilters}
            onChange={setBuFilters}
            onFilter={handleBuFilter}
          />
          <DataTable
            columns={BY_DATE_COLUMNS}
            rows={buRows}
            loading={buLoading}
          />
          <Pagination
            pagination={buPagination}
            currentLimit={buLimit}
            onPageChange={(p) => fetchByDate(p, buLimit, buFilters)}
            onLimitChange={(l) => {
              setBuLimit(l);
              fetchByDate(1, l, buFilters);
            }}
          />
        </div>
      )}

      {/* Tab: By Assigner */}
      {activeTab === "assigner" && (
        <div>
          <FilterBar
            filters={asFilters}
            onChange={setAsFilters}
            onFilter={handleAsFilter}
          />
          <DataTable
            columns={BY_ASSIGNER_COLUMNS}
            rows={asRows}
            loading={asLoading}
          />
          <Pagination
            pagination={asPagination}
            currentLimit={asLimit}
            onPageChange={(p) => fetchByAssigner(p, asLimit, asFilters)}
            onLimitChange={(l) => {
              setAsLimit(l);
              fetchByAssigner(1, l, asFilters);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default PlatformSignatureRecapDetails;
